<template>
  <div v-if="data" class="flex flex-col gap-5">
    <h2 class="text-2xl font-bold text-white">Circulating Supply Breakdown</h2>
    <div class="bg-white/5 border border-white/20 rounded-lg flex flex-col flex-1">
      <!-- Chart Container -->
      <div class="p-8 flex items-center justify-center">
        <highcharts class="w-full" :options="chartOptions" />
      </div>

      <!-- Powered by -->
      <div class="px-4 py-4">
        <span class="text-base font-normal text-white/40">Powered by Xangle</span>
      </div>

      <!-- Info Section -->
      <div class="flex flex-col">
        <!-- Circulating Supply -->
        <div class="border-t border-white/20 px-4 py-4">
          <div class="flex flex-row items-center justify-between mobile:flex-col mobile:items-start mobile:gap-2">
            <span class="text-lg font-bold text-white">Circulating Supply</span>
            <div class="flex items-center gap-1">
              <span class="text-[1.25rem] font-bold text-white">{{ setCommaWithDigits(data.circulatingSupply, 0) }}</span>
              <span class="text-[1.25rem] font-bold text-white/40">NXPC</span>
            </div>
          </div>
        </div>

        <!-- Sub items -->
        <div class="flex flex-col">
          <!-- Converted to NESO -->
          <div class="px-4 py-2 pl-6">
            <div class="flex flex-row items-center justify-between mobile:flex-col mobile:items-start mobile:gap-1">
              <div class="flex items-center gap-2">
                <span class="w-3 h-3 rounded-full" :style="{ backgroundColor: data.lockedInNeso.color }" />
                <span class="text-lg font-bold text-white">Converted to NESO</span>
                <span class="text-lg font-bold text-white/40">({{ data.lockedInNeso.percent }}%)</span>
              </div>
              <div class="flex items-center gap-1">
                <span class="text-xl font-bold text-white">{{ setCommaWithDigits(data.lockedInNeso.value, 0) }}</span>
                <span class="text-xl font-bold text-white/40">NXPC</span>
              </div>
            </div>
          </div>

          <!-- Converted to NFTs -->
          <div class="px-4 py-2 pl-6">
            <div class="flex flex-row items-center justify-between mobile:flex-col mobile:items-start mobile:gap-1">
              <div class="flex items-center gap-2">
                <span class="w-3 h-3 rounded-full" :style="{ backgroundColor: data.lockedInFission.color }" />
                <span class="text-lg font-bold text-white">Converted to NFTs</span>
                <span class="text-lg font-bold text-white/40">({{ data.lockedInFission.percent }}%)</span>
              </div>
              <div class="flex items-center gap-1">
                <span class="text-xl font-bold text-white">{{ setCommaWithDigits(data.lockedInFission.value, 0) }}</span>
                <span class="text-xl font-bold text-white/40">NXPC</span>
              </div>
            </div>
          </div>

          <!-- Circulating as NXPC -->
          <div class="px-4 py-2 pl-6">
            <div class="flex flex-row items-center justify-between mobile:flex-col mobile:items-start mobile:gap-1">
              <div class="flex items-center gap-2">
                <span class="w-3 h-3 rounded-full" :style="{ backgroundColor: data.freeFloat.color }" />
                <span class="text-lg font-bold text-white">Circulating as NXPC</span>
                <span class="text-lg font-bold text-white/40">({{ data.freeFloat.percent }}%)</span>
              </div>
              <div class="flex items-center gap-1">
                <span class="text-xl font-bold text-white">{{ setCommaWithDigits(data.freeFloat.value, 0) }}</span>
                <span class="text-xl font-bold text-white/40">NXPC</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Divider -->
        <div class="mx-4 my-4 h-px bg-white/20" />

        <!-- Burned -->
        <div class="px-4 pb-4 pl-6">
          <div class="flex flex-row items-center justify-between mobile:flex-col mobile:items-start mobile:gap-1">
            <div class="flex items-center gap-2">
              <span class="w-3 h-3 rounded-full bg-[#ff6f91]" />
              <span class="text-lg font-bold text-white">Burned</span>
            </div>
            <div class="flex items-center gap-1">
              <span class="text-[1.25rem] font-bold text-white">{{ setCommaWithDigits(data.burned, 0) }}</span>
              <span class="text-[1.25rem] font-bold text-white/40">NXPC</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { setCommaWithDigits } from "~/utils/numberFormat";
import {
  type CirculatingSupplyBreakdownView,
  BURNED_COLOR,
} from "~/api/service/livewatch/circulating-supply-breakdown.service";

const props = defineProps<{
  data: CirculatingSupplyBreakdownView | undefined;
}>();

const { createCustomTooltip } = useChartTooltip();

const chartOptions = computed(() => {
  if (!props.data) return {};

  const data = props.data;
  return {
    chart: {
      type: "bar",
      height: 200,
      backgroundColor: "transparent",
      spacing: [10, 10, 10, 10],
    },
    title: { text: undefined },
    credits: { enabled: false },
    legend: { enabled: false },
    xAxis: {
      categories: ["Circulating Supply", "Burned"],
      labels: {
        style: { color: "#9CA3AF", fontSize: "12px", fontFamily: "Montserrat" },
      },
      lineColor: "transparent",
      tickColor: "transparent",
    },
    yAxis: {
      title: { text: undefined },
      labels: {
        style: { color: "#9CA3AF", fontSize: "12px", fontFamily: "Montserrat" },
        formatter: function (this: any) {
          return `${(this.value / 1000000).toFixed(0)}M`;
        },
      },
      gridLineColor: "rgba(255,255,255,0.1)",
      max: Math.ceil(data.circulatingSupply / 100000000) * 100000000,
    },
    plotOptions: {
      series: {
        stacking: "normal",
        borderWidth: 0,
        borderRadius: 4,
        pointWidth: 36,
        dataLabels: { enabled: false },
      },
    },
    tooltip: {
      shared: false,
      useHTML: true,
      outside: true,
      backgroundColor: "none",
      borderWidth: 0,
      shadow: false,
      style: { fontSize: "12px", fontFamily: "Montserrat" },
      formatter: function (this: any) {
        const value = this.y;
        const total =
          this.point.category === "Circulating Supply"
            ? data.circulatingSupply
            : data.burned;
        const percent = ((value / total) * 100).toFixed(1);
        const isBurned = this.series.name === "Burned";
        return createCustomTooltip(this, {
          containerClass: "w-[240px]",
          content: () => `
            <div class="flex flex-col gap-1">
              <div class="flex items-center gap-1">
                <span style="color:${this.color};">●</span>
                <span class="font-bold">${this.series.name}</span>
              </div>
              <div class="flex justify-between">
                <span>${setCommaWithDigits(value, 0)} NXPC${isBurned ? "" : ` (${percent}%)`}</span>
              </div>
            </div>
          `,
        });
      },
    },
    series: [
      {
        name: "Circulating as NXPC",
        data: [data.freeFloat.value, 0],
        color: data.freeFloat.color,
      },
      {
        name: "Converted to NFTs",
        data: [data.lockedInFission.value, 0],
        color: data.lockedInFission.color,
      },
      {
        name: "Converted to NESO",
        data: [data.lockedInNeso.value, 0],
        color: data.lockedInNeso.color,
      },
      {
        name: "Burned",
        data: [0, data.burned],
        color: BURNED_COLOR,
      },
    ],
  };
});
</script>
